import { concat } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';

import { Customer } from '../../../../selectors/select-customer/types';
import { PRODUCTS_WITH_SERVICES } from '../../queries/fetch-products-with-services/query.gql';
import client from '../../../../graphql/client';
import getProductIdsWithFewerInclusions from '../get-product-ids-with-fewer-inclusions';
import getSaleableProductIds from '../../../../utils/get-saleable-product-ids';
import ProductActions from '../../../../actions/product';

type useGetSuitableProductIdsProps = {
  scale: Customer['scale'];
  extrasServices: string[];
  hospitalServices: string[];
};

const useGetSuitableProductIds = ({ scale, extrasServices, hospitalServices }: useGetSuitableProductIdsProps) => {
  const dispatch = useDispatch();

  const { data, loading, error } = useQuery(PRODUCTS_WITH_SERVICES, {
    client,
    variables: {
      extrasInput: {
        productType: PRODUCT_TYPE.EXTRAS,
        serviceIds: extrasServices,
      },
      hospitalInput: {
        productType: PRODUCT_TYPE.HOSPITAL,
        serviceIds: hospitalServices,
      },
    },
  });

  useEffect(() => {
    if (!data || loading || error) {
      return;
    }

    const saleableProductIds = getSaleableProductIds();
    const productIdsToLoad = concat(data.extras ?? [], saleableProductIds);
    dispatch(ProductActions.load(productIdsToLoad));
  }, [data, dispatch, error, loading]);

  const extrasData = data?.extras ?? [];
  const hospitalData = data?.hospital ?? [];

  const { productIds: extrasProductIds, hasProductWithFewerInclusions: hasExtrasWithFewerInclusions } =
    getProductIdsWithFewerInclusions(loading, Boolean(error), extrasData, PRODUCT_TYPE.EXTRAS, scale);
  const { productIds: hospitalParentProductIds, hasProductWithFewerInclusions: hasHospitalWithFewerInclusions } =
    getProductIdsWithFewerInclusions(loading, Boolean(error), hospitalData, PRODUCT_TYPE.HOSPITAL, scale);

  const isLoading = loading;
  const hasError = Boolean(error);

  return {
    extrasProductIds,
    hasError,
    hasExtrasWithFewerInclusions,
    hasHospitalWithFewerInclusions,
    hospitalParentProductIds,
    isLoading,
  };
};

export default useGetSuitableProductIds;
